/* fillerSchedule.js — derived FILLER CUT-LIST (v0.53). DERIVED, NEVER PERSISTED.
 *
 * A sibling of endPanels.js and getWorktopRuns: a pure, read-only, additive
 * manufacturing output. Professional CAD (Cabinet Vision / 2020 / Microvellum)
 * emits a filler as a cut-list line item — a strip of finished door/panel stock
 * that closes a gap too small for a cabinet. A filler is NOT a placement in the
 * saved design (that would be a serialization change, deferred); it is a value
 * the layer DERIVES on demand from the existing placement model + wall topology.
 * Nothing is written back: no schema/placement/UI/serialization change, no mutation.
 *
 * TWO FILLER CLASSES (both LINEAR — same wall, same line):
 *   (1) GAP filler — a run-internal dead-band between two consecutive same-wall,
 *       same-line members whose edge gap g ∈ [FILLER_MIN, FILLER_MAX]. Derived from
 *       the consecutive-member (byWallLine) pattern the rulebook already uses
 *       (gap.below-min-filler, gap.worktop-discontinuity), NOT from runs.js within-run
 *       adjacency: a fillable gap (g > RUN_GAP_TOL) BREAKS the flush chain, so the two
 *       neighbours sit in DIFFERENT run segments — a filler is the thing BETWEEN run
 *       segments, not inside one, so the endPanels within-run-connectivity template
 *       does not transfer. This generalizes worktop-discontinuity (base-only) to base+upper.
 *   (2) END filler (scribe) — the residual between a run terminal's outer edge and the
 *       wall inner-corner at a wall RETURN (wallInnerSpan off > 0). Mirrors the geometry
 *       of gap.excessive-end (first.left − off0 at start, len − off1 − last.right at end).
 *
 * DISJOINTNESS (the whole point — no part double-counts another finding/part):
 *   - vs gap.below-min-filler: that rule owns g ∈ (RUN_GAP_TOL, FILLER_MIN); we start at
 *     >= FILLER_MIN. Inclusive boundary matches the rulebook (worktop-discontinuity uses
 *     the same `gap >= FILLER_MIN`). Byte-consistent at the boundary.
 *   - vs cabinet-sized gaps / gap.excessive-end: those own g > FILLER_MAX; we stop at
 *     <= FILLER_MAX. Only [FILLER_MIN, FILLER_MAX] is ever a filler.
 *   - vs endPanels finished END panels (the return-side single-oracle reconciliation):
 *     endPanels declares a return-side terminal a SCRIBE (suppresses its finished panel)
 *     only within END_PANEL_CORNER_TOL_M of the return, and emits a finished END PANEL
 *     beyond that. A return-side residual in (END_PANEL_CORNER_TOL_M, FILLER_MAX] would
 *     otherwise be BOTH an end panel AND an end filler — the same location, two parts.
 *     We resolve on the ONE shared threshold endPanels already uses: the END-filler class
 *     is capped at END_PANEL_CORNER_TOL_M, so it produces exactly the scribe endPanels
 *     declined to panel, and cedes the (tol, FILLER_MAX] band to the finished panel.
 *     Result: exactly one part per return-side terminal. (If endPanels later raises its
 *     abutsReturn tolerance to FILLER_MAX, widen this cap to FILLER_MAX to match — a
 *     one-line change gated on that edit.) Interior GAP fillers do not touch a return and
 *     need no such handling.
 *
 * CORNER fillers (a strip spanning a JOINT between two DIFFERENT walls) are NOT linear and
 * are therefore NEITHER emitted NOR double-counted here — they are sourced separately from
 * the corner-void telemetry (gap.corner-void) / corner-filler suggestedFix (ruleEngine
 * ~267). Deferred by design; this schedule is explicitly the LINEAR (same-wall/line) set.
 *
 * Dual-line (tall) members: a tall unit is on both base and upper lines, so a tall↔tall
 * gap and a tall terminal appear twice. We dedupe to the BASE pass (one full-height
 * filler), exactly as ruleEngine's consecutivePairs does.
 *
 * Cost: one memoized getWallGraph pass + O(P log P). Build once per interaction.
 */
import { linesOfRole } from "./runs";
import { wallInnerSpan } from "./snapping";
import { effDef } from "./pricing";
import { getWallGraph } from "./wallGraph";
import { LHC_RULES } from "./constants";
import { END_PANEL_CORNER_TOL_M } from "./endPanels";
import { golaGapBetween } from "./golaClearance";
import { pairPanelGap } from "./sidePanelClearance";

const LINES = ["base", "upper"];

/** buildFillerSchedule(walls, placements, lib) → FillerPiece[].
 *  FillerPiece = { wallId, line:"base"|"upper", kind:"gap"|"end", widthMm, heightMm,
 *  afterPlacementId|null, beforePlacementId|null, atEnd:"start"|"end"|null, reason }.
 *  DERIVED, pure, no mutation. Deterministic order (wallId, line, then along-wall position). */
export function buildFillerSchedule(walls, placements, lib) {
  const wls = walls || [];
  const pls = placements || [];
  const graph = getWallGraph(wls);

  // Eligible members — mirror runs.js / ruleEngine.buildRuleContext exactly (same
  // eligibility so the schedule and the rulebook see the identical member set).
  const members = [];
  pls.forEach((p) => {
    if (!p.wallId || p.island) return;
    const def = effDef(p, lib);
    if (!def || !graph.walls.get(p.wallId)) return;
    const lines = linesOfRole(def.role);
    if (!lines.length) return;
    const left = p.absLeft || 0;
    members.push({ p, def, lines, left, right: left + (def.width || 0), wallId: p.wallId });
  });

  // wallId → { base:[idx], upper:[idx] } sorted by left edge (the byWallLine pattern).
  const byWallLine = new Map();
  members.forEach((m, i) => {
    if (!byWallLine.has(m.wallId)) byWallLine.set(m.wallId, { base: [], upper: [] });
    m.lines.forEach((ln) => byWallLine.get(m.wallId)[ln].push(i));
  });
  byWallLine.forEach((lines) => {
    lines.base.sort((a, b) => members[a].left - members[b].left);
    lines.upper.sort((a, b) => members[a].left - members[b].left);
  });

  const FMIN = LHC_RULES.FILLER_MIN, FMAX = LHC_RULES.FILLER_MAX;
  const heightMm = (def) => Math.round((def.height || 0) * 1000);
  const widthMm = (g) => Math.round(g * 1000);
  // A dual-line member is processed on the BASE pass; skip its UPPER duplicate.
  const skipUpperDup = (line, ...ms) => line === "upper" && ms.every((m) => m.lines.includes("base"));

  const out = [];

  byWallLine.forEach((lines, wallId) => {
    const sp = wallInnerSpan(wls.find((w) => w.id === wallId), wls);
    const wi = graph.walls.get(wallId);

    LINES.forEach((line) => {
      const idxs = lines[line];
      if (!idxs.length) return;

      // ── (1) interior GAP fillers between consecutive same-line members ────────
      for (let k = 0; k < idxs.length - 1; k++) {
        const a = members[idxs[k]], b = members[idxs[k + 1]];
        if (skipUpperDup(line, a, b)) continue; // tall↔tall counted once, on base
        // a gola channel between them is construction, not a filler (2026-08-30)
        // a gola channel OR a side panel between them is construction, not a filler
        const gap = b.left - a.right - golaGapBetween(a.p, b.p, pls, (x) => effDef(x, lib))
          - pairPanelGap(a.p, a.def, b.p, b.def);
        if (gap >= FMIN && gap <= FMAX) {
          out.push({
            wallId, line, kind: "gap",
            widthMm: widthMm(gap),
            heightMm: Math.max(heightMm(a.def), heightMm(b.def)), // taller adjacent member
            afterPlacementId: a.p.id, beforePlacementId: b.p.id, atEnd: null,
            reason: "inter-cabinet-gap-filler",
            _pos: a.right,
          });
        }
      }

      // ── (2) wall-return END (scribe) fillers at each run terminal ─────────────
      if (!sp || !wi) return;
      const first = members[idxs[0]], last = members[idxs[idxs.length - 1]];

      // start-return terminal (nearest wall start)
      if (!skipUpperDup(line, first) && sp.off0 > 0) {
        const gap = first.left - sp.off0;
        // Capped at END_PANEL_CORNER_TOL_M (not FILLER_MAX): the single shared oracle
        // with endPanels — exactly the scribe endPanels declined to make a finished panel.
        if (gap >= FMIN && gap <= END_PANEL_CORNER_TOL_M) {
          out.push({
            wallId, line, kind: "end",
            widthMm: widthMm(gap), heightMm: heightMm(first.def),
            afterPlacementId: null, beforePlacementId: first.p.id, atEnd: "start",
            reason: "wall-return-scribe-filler",
            _pos: sp.off0,
          });
        }
      }

      // end-return terminal (nearest wall end)
      if (!skipUpperDup(line, last) && sp.off1 > 0) {
        const gap = wi.len - sp.off1 - last.right;
        if (gap >= FMIN && gap <= END_PANEL_CORNER_TOL_M) {
          out.push({
            wallId, line, kind: "end",
            widthMm: widthMm(gap), heightMm: heightMm(last.def),
            afterPlacementId: last.p.id, beforePlacementId: null, atEnd: "end",
            reason: "wall-return-scribe-filler",
            _pos: wi.len - sp.off1,
          });
        }
      }
    });
  });

  out.sort((a, b) => {
    if (a.wallId !== b.wallId) return a.wallId < b.wallId ? -1 : 1;
    const lr = (a.line === "base" ? 0 : 1) - (b.line === "base" ? 0 : 1);
    if (lr !== 0) return lr;
    if (a._pos !== b._pos) return a._pos - b._pos;
    // stable tie-break: gap before end, then by anchor id
    if (a.kind !== b.kind) return a.kind < b.kind ? -1 : 1;
    const ak = (a.afterPlacementId || a.beforePlacementId || "");
    const bk = (b.afterPlacementId || b.beforePlacementId || "");
    return ak < bk ? -1 : ak > bk ? 1 : 0;
  });

  return out.map(({ _pos, ...f }) => f); // drop the internal sort key
}
